import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import {
  WAITING_WORK_FIRST_ROWS,
  WAITING_WORK_PAGE_LIMIT,
  type AssessmentId,
  type AssessmentInProgressRow,
  type DateOfBirth,
  type PersonId,
  type UnassignedPersonRow,
  type WaitingWorkSlice,
} from "@thalamos/haven-common";

import type { ProgressReading } from "../../api/formDraft/readAssessmentProgress.js";
import { ageInYears } from "../../components/PersonBanner/PersonBanner.js";
import { createRoute } from "../../routing/createRoute.js";

/** How many characters of a return's reason a row quotes. */
const REASON_EXCERPT_LENGTH = 120;

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/** A row as shown: the young person, what it is, its detail, the user's part and where its one button goes. */
type ShownRow = {
  key: string;
  personId: PersonId;
  personName: string;
  dateOfBirth: DateOfBirth | null;
  what: string;
  tag: string | null;
  detail: string;
  part: string;
  to: string;
  actionLabel: string;
};

const quoted = (reason: string): string =>
  reason.length > REASON_EXCERPT_LENGTH
    ? `${reason.slice(0, REASON_EXCERPT_LENGTH).trimEnd()}…`
    : reason;

/** What an assessment under way is, how long since it was returned, and what to say of it. */
const inProgressText = ({
  t,
  row,
  progress,
  now,
}: {
  t: TFunction;
  row: AssessmentInProgressRow;
  progress: ProgressReading | undefined;
  now: Date;
}): Pick<ShownRow, "what" | "tag" | "detail" | "part"> => {
  const started = CALENDAR_DATE.format(row.startedAt);
  const sinceStarted =
    progress?.kind === "read"
      ? t("yourActions.rows.progress", {
          completed: progress.completed,
          included: progress.included,
          date: started,
        })
      : t("yourActions.rows.started", { date: started });
  const designatedAssessor = t("access.kind.designated_assessor");

  return match(row.stage)
    .with({ kind: "returned" }, (returned) => {
      const days = dayjs(now).diff(dayjs(returned.returnedAt), "day");

      return {
        what: t("yourActions.rows.returned"),
        tag:
          days === 0
            ? t("yourActions.rows.returnedToday")
            : t("yourActions.rows.returnedAgo", { count: days }),
        detail: t("yourActions.rows.returnReason", {
          approverName: returned.approverName,
          reason: quoted(returned.reason),
        }),
        part: designatedAssessor,
      };
    })
    .with({ kind: "definitive" }, () => ({
      what: t("yourActions.rows.definitive"),
      tag: null,
      detail: sinceStarted,
      part: designatedAssessor,
    }))
    .with({ kind: "non_definitive" }, () => ({
      what: t("yourActions.rows.nonDefinitive"),
      tag: null,
      detail: sinceStarted,
      part: t("roles.assessor"),
    }))
    .exhaustive();
};

/** Whom an unassigned young person still needs, and what that holds up. */
const unassignedText = ({
  t,
  row,
}: {
  t: TFunction;
  row: UnassignedPersonRow;
}): Pick<ShownRow, "what" | "detail"> => ({
  what: t("yourActions.rows.stillNeeds", {
    missing: row.missing.map((kind) => t(`access.kind.${kind}`)).join(", "),
  }),
  detail: row.assessmentWaiting
    ? t("yourActions.rows.cannotStartWhileWaiting", {
        count: row.missing.length,
      })
    : t("yourActions.rows.cannotStart", { count: row.missing.length }),
});

const shownRowsOf = ({
  t,
  slice,
  progressOf,
  now,
}: {
  t: TFunction;
  slice: WaitingWorkSlice;
  progressOf: (assessmentId: AssessmentId) => ProgressReading | undefined;
  now: Date;
}): ShownRow[] =>
  match(slice)
    .with({ group: "new_assessments" }, ({ rows }) =>
      rows.map((row) => ({
        key: row.personId,
        personId: row.personId,
        personName: row.personName,
        dateOfBirth: row.dateOfBirth,
        what: t("yourActions.rows.firstAssessment"),
        tag: null,
        detail: t("yourActions.rows.designatedOn", {
          date: CALENDAR_DATE.format(row.designatedAt),
        }),
        part: t("access.kind.designated_assessor"),
        // Starting is offered on the Assessments tab, where the instrument
        // is chosen.
        to: createRoute.personTab(row.personId, "assessments"),
        actionLabel: t("yourActions.rows.buttons.start"),
      })),
    )
    .with({ group: "assessments_in_progress" }, ({ rows }) =>
      rows.map((row) => ({
        key: row.assessmentId,
        personId: row.personId,
        personName: row.personName,
        dateOfBirth: row.dateOfBirth,
        ...inProgressText({
          t,
          row,
          progress: progressOf(row.assessmentId),
          now,
        }),
        to: createRoute.assessment(row.assessmentId),
        actionLabel: t("yourActions.rows.buttons.continue"),
      })),
    )
    .with({ group: "assessments_for_review" }, ({ rows }) =>
      rows.map((row) => ({
        key: row.assessmentId,
        personId: row.personId,
        personName: row.personName,
        dateOfBirth: row.dateOfBirth,
        what: t("yourActions.rows.definitive"),
        tag: row.resubmitted ? t("yourActions.rows.resubmitted") : null,
        detail: t("yourActions.rows.submittedBy", {
          assessorName: row.assessorName,
          date: CALENDAR_DATE.format(dayjs(row.assessedOn).toDate()),
        }),
        part: t("roles.approver"),
        to: createRoute.assessment(row.assessmentId),
        actionLabel: t("yourActions.rows.buttons.review"),
      })),
    )
    .with({ group: "unassigned_persons" }, ({ rows }) =>
      rows.map((row) => ({
        key: row.personId,
        personId: row.personId,
        personName: row.personName,
        dateOfBirth: row.dateOfBirth,
        ...unassignedText({ t, row }),
        tag: null,
        part: t("roles.responsible_user"),
        to: createRoute.personTab(row.personId, "case-involvement"),
        actionLabel: t("yourActions.rows.buttons.assignPeople"),
      })),
    )
    .exhaustive();

const WaitingWorkRow = ({
  row,
  filled,
}: {
  row: ShownRow;
  filled: boolean;
}) => {
  const { t } = useTranslation();
  const age = ageInYears(row.dateOfBirth);

  return (
    <Box
      component="li"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "220px minmax(0, 1fr) 150px 140px",
        },
        gap: 2,
        alignItems: "center",
        px: 2.25,
        py: 1.75,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        listStyle: "none",
      }}
    >
      <Stack spacing={0.25}>
        <Link
          component={RouterLink}
          to={createRoute.person(row.personId)}
          sx={{ fontWeight: 600 }}
        >
          {row.personName}
        </Link>
        {row.dateOfBirth === null || age === undefined ? null : (
          <Typography variant="body2" color="text.secondary">
            {t("yourActions.bornAged", {
              dateOfBirth: CALENDAR_DATE.format(
                dayjs(row.dateOfBirth).toDate(),
              ),
              age,
            })}
          </Typography>
        )}
      </Stack>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={{ fontWeight: 600 }}>{row.what}</Typography>
          {row.tag === null ? null : <Chip size="small" label={row.tag} />}
        </Stack>
        <Typography variant="body2">{row.detail}</Typography>
      </Stack>
      <Stack spacing={0.125}>
        <Typography variant="overline" color="text.secondary">
          {t("yourActions.yourPart")}
        </Typography>
        <Typography variant="body2">{row.part}</Typography>
      </Stack>
      <Button
        component={RouterLink}
        to={row.to}
        variant={filled ? "contained" : "outlined"}
        aria-label={t("yourActions.rows.buttonFor", {
          action: row.actionLabel,
          personName: row.personName,
        })}
        sx={{ justifySelf: { md: "end" } }}
      >
        {row.actionLabel}
      </Button>
    </Box>
  );
};

/**
 * One group of what is waiting: its heading with the full total, its rows in
 * the group's order, and the controls that show more of it.
 *
 * `first` is what the page loaded for the group. `expanded` is what Show all
 * and Show more have loaded since, which replaces it while the group is open.
 */
export const WaitingWorkGroupSection = ({
  first,
  expanded,
  progressOf,
  now,
  onShowAll,
  onShowMore,
  onShowFewer,
}: {
  first: WaitingWorkSlice;
  expanded: WaitingWorkSlice | undefined;
  progressOf: (assessmentId: AssessmentId) => ProgressReading | undefined;
  now: Date;
  onShowAll: () => Promisable<void>;
  onShowMore: () => Promisable<void>;
  onShowFewer: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const shown = expanded ?? first;
  const rows = shownRowsOf({ t, slice: shown, progressOf, now });
  const { total, group } = first;
  const headingId = `waiting-work-${group}`;

  return (
    <Stack
      component="section"
      id={group}
      aria-labelledby={headingId}
      spacing={1.25}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
        <Typography id={headingId} variant="h6" component="h2">
          {t(`yourActions.groups.${group}.title`)}
        </Typography>
        <Chip size="small" color="primary" label={total} />
        <Typography variant="body2" color="text.secondary">
          {t(`yourActions.groups.${group}.subtitle`)}
        </Typography>
      </Stack>

      <Stack component="ul" spacing={1} sx={{ p: 0, m: 0 }}>
        {rows.map((row, index) => (
          <WaitingWorkRow key={row.key} row={row} filled={index === 0} />
        ))}
      </Stack>

      {expanded === undefined ? (
        total > WAITING_WORK_FIRST_ROWS ? (
          <Link
            component="button"
            onClick={() => void onShowAll()}
            sx={{ alignSelf: "flex-start" }}
          >
            {total <= WAITING_WORK_PAGE_LIMIT
              ? t("yourActions.showAll", {
                  count: total,
                  group: t(`yourActions.groups.${group}.lower`),
                })
              : t("yourActions.showMoreOfGroup", {
                  count: WAITING_WORK_PAGE_LIMIT - rows.length,
                  group: t(`yourActions.groups.${group}.lower`),
                })}
          </Link>
        ) : null
      ) : (
        <Stack direction="row" spacing={2} alignItems="center">
          {rows.length < total ? (
            <>
              <Link component="button" onClick={() => void onShowMore()}>
                {t("yourActions.showMore", {
                  count: Math.min(WAITING_WORK_PAGE_LIMIT, total - rows.length),
                })}
              </Link>
              <Typography variant="body2" color="text.secondary">
                {t("yourActions.showing", { shown: rows.length, total })}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("yourActions.showingAll", { total })}
            </Typography>
          )}
          <Link component="button" onClick={() => void onShowFewer()}>
            {t("yourActions.showFewer")}
          </Link>
        </Stack>
      )}
    </Stack>
  );
};
